import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Sidebar order mirrors ALL_NAV in Layout.jsx (manager-only pages excluded — they
// are never a good post-login landing). Returns the first menu the role can see.
const LANDING_NAV = [
  { to: "/", key: "dashboard" },
  { to: "/leads", key: "leads" },
  { to: "/projects", key: "projects" },
  { to: "/attention", key: "attention" },
  { to: "/policies", key: "policies" },
  { to: "/performance", key: "performance" },
  { to: "/finance", key: "finance" },
  { to: "/team", key: "team" },
  { to: "/audit", key: "audit" },
];
export function firstVisiblePath(perms, isManager) {
  if (!perms || perms.super || isManager) return "/";
  const sb = perms.sidebar || {};
  const item = LANDING_NAV.find((n) => (sb[n.key] ?? "none") !== "none");
  return item ? item.to : "/";
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauthenticated, obj = authed
  const [ready, setReady] = useState(false);
  const [perms, setPerms] = useState(null);

  const loadPerms = () => api.get("/permissions/me").then((r) => { setPerms(r.data); return r.data; }).catch(() => { setPerms(null); return null; });

  useEffect(() => {
    const token = localStorage.getItem("daa_token");
    if (!token) {
      setUser(false);
      setReady(true);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => { setUser(r.data); return loadPerms(); })
      .catch(() => {
        localStorage.removeItem("daa_token");
        setUser(false);
      })
      .finally(() => setReady(true));
  }, []);

  // Sliding session: renew the 7-day token on load and every 6 hours while the app is open.
  useEffect(() => {
    if (!user) return undefined;
    const renew = () => api.post("/auth/renew").then((r) => { if (r.data?.token) localStorage.setItem("daa_token", r.data.token); }).catch(() => {});
    renew();
    const t = setInterval(renew, 6 * 60 * 60 * 1000);
    return () => clearInterval(t);
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password }, { skipBlocked: true });
    localStorage.setItem("daa_token", data.token);
    // Fresh login resets view preferences so each sign-in starts at role defaults.
    ["daa_scope", "daa_projects_scope", "daa_perf_window"].forEach((k) => localStorage.removeItem(k));
    setUser(data.user);
    const p = await loadPerms();
    return { user: data.user, perms: p };
  };

  const changePassword = async (current_password, new_password) => {
    const { data } = await api.post("/auth/change-password", { current_password, new_password }, { skipBlocked: true });
    localStorage.setItem("daa_token", data.token);
    const r = await api.get("/auth/me");
    setUser(r.data);
    return true;
  };

  const logout = () => {
    localStorage.removeItem("daa_token");
    localStorage.removeItem("daa_admin_token");
    ["daa_scope", "daa_projects_scope", "daa_perf_window"].forEach((k) => localStorage.removeItem(k));
    setUser(false);
    setPerms(null);
    window.location.href = "/login";
  };

  const impersonate = async (userId) => {
    const { data } = await api.post(`/users/${userId}/impersonate`);
    localStorage.setItem("daa_admin_token", localStorage.getItem("daa_token"));
    localStorage.setItem("daa_token", data.token);
    ["daa_scope", "daa_projects_scope", "daa_perf_window"].forEach((k) => localStorage.removeItem(k));
    window.location.href = "/";
  };

  const stopImpersonate = async () => {
    try { await api.post("/auth/impersonate/stop"); } catch { /* best-effort audit */ }
    const adminToken = localStorage.getItem("daa_admin_token");
    if (adminToken) {
      localStorage.setItem("daa_token", adminToken);
      localStorage.removeItem("daa_admin_token");
    }
    ["daa_scope", "daa_projects_scope", "daa_perf_window"].forEach((k) => localStorage.removeItem(k));
    window.location.href = "/";
  };

  const isManager = user && (user.role === "Admin" || user.role === "Managing Director");

  // Permission-aware helpers. While perms are still loading we stay permissive
  // (backend enforces the real gate) to avoid a visible flash of hidden items.
  const sidebar = (key) => {
    if (!perms) return "use";
    return (perms.sidebar || {})[key] ?? "none";
  };
  const can = (moduleKey, action = "view") => {
    if (!perms) return true;
    if (perms.super) return true;
    const acts = (perms.modules || {})[moduleKey] || [];
    if (!acts.includes("view")) return false; // no View => all other actions void
    return acts.includes(action);
  };
  // Access is governed strictly by the role permission matrix — no project-assignment
  // elevation. canInProject is kept as an alias of can for call-site compatibility.
  const canInProject = (project, moduleKey, action = "view") => can(moduleKey, action);

  const landingPath = () => firstVisiblePath(perms, isManager);

  const canTeam = (action = "view") => {
    if (!perms) return true;
    if (perms.super) return true;
    const lvl = (perms.sidebar || {}).team || "none";
    if (lvl === "none") return false;
    if (action === "view") return true;
    return (perms.team_actions || []).includes(action);
  };

  const canPolicy = (action = "view") => {
    if (!perms) return true;
    if (perms.super) return true;
    const lvl = (perms.sidebar || {}).policies || "none";
    if (lvl === "none") return false;
    if (action === "view") return true;
    return (perms.policy_actions || []).includes(action);
  };

  const canFeature = (key) => {
    if (!perms) return true;
    if (perms.super) return true;
    return (perms.features || []).includes(key);
  };

  const canCreateProject = !perms ? true : (perms.super || !!(perms.project_access || {}).create);
  const canViewAllProjects = !perms ? true : (perms.super || !!(perms.project_access || {}).view_all);
  const canAccessAllProjects = !perms ? true : (perms.super || !!(perms.project_access || {}).access_all);
  const canDeleteProject = !perms ? true : (perms.super || !!(perms.project_access || {}).delete);
  const canViewDeleted = !perms ? true : (perms.super || !!(perms.project_access || {}).view_deleted);
  const canRestoreProject = !perms ? true : (perms.super || !!(perms.project_access || {}).restore);
  const canPurgeProject = !perms ? true : (perms.super || !!(perms.project_access || {}).purge);
  const canAssignTeam = !perms ? true : (perms.super || !!(perms.project_access || {}).assign_team);
  const canManageSupporting = !perms ? true : (perms.super || !!(perms.project_access || {}).manage_supporting);
  const canChangeStatus = !perms ? true : (perms.super || !!(perms.project_access || {}).change_status);
  const canCreateLead = !perms ? true : (perms.super || !!(perms.lead_access || {}).create);
  const canViewAllLeads = !perms ? true : (perms.super || !!(perms.lead_access || {}).view_all);
  const canAccessAllLeads = !perms ? true : (perms.super || !!(perms.lead_access || {}).access_all);
  const canDeleteLead = !perms ? true : (perms.super || !!(perms.lead_access || {}).delete);
  const canViewDeletedLeads = !perms ? true : (perms.super || !!(perms.lead_access || {}).view_deleted);
  const canRestoreLead = !perms ? true : (perms.super || !!(perms.lead_access || {}).restore);
  const canPurgeLead = !perms ? true : (perms.super || !!(perms.lead_access || {}).purge);
  const canAssignLead = !perms ? true : (perms.super || !!(perms.lead_access || {}).assign);
  const canRequestLead = !perms ? false : (perms.super || !!(perms.lead_access || {}).request);
  const canApproveLeadRequest = !perms ? false : (perms.super || (perms.approvals || []).includes("lead_request"));
  const canManageSupportingLead = !perms ? true : (perms.super || !!(perms.lead_access || {}).manage_supporting);
  const canChangeLeadStatus = !perms ? true : (perms.super || !!(perms.lead_access || {}).change_status);
  const canEditLeadAutoDate = !perms ? true : (perms.super || !!(perms.lead_access || {}).edit_auto_date);
  const canEditProjectAutoDate = !perms ? true : (perms.super || !!(perms.project_access || {}).edit_auto_date);
  const canViewSensitive = !perms ? true : (perms.super || !!(perms.project_access || {}).view_sensitive);
  const canViewClientInfo = !perms ? true : (perms.super || !!(perms.project_access || {}).view_client_info);

  return (
    <AuthContext.Provider value={{ user, ready, login, logout, impersonate, stopImpersonate, changePassword, mustChangePassword: !!(user && user.must_change_password), impersonating: !!(user && user._impersonated), impersonator: user && user._impersonator, isManager, perms, sidebar, landingPath, can, canInProject, canTeam, canPolicy, canFeature, canCreateProject, canViewAllProjects, canAccessAllProjects, canDeleteProject, canViewDeleted, canRestoreProject, canPurgeProject, canAssignTeam, canManageSupporting, canChangeStatus, canCreateLead, canViewAllLeads, canAccessAllLeads, canDeleteLead, canViewDeletedLeads, canRestoreLead, canPurgeLead, canAssignLead, canRequestLead, canApproveLeadRequest, canManageSupportingLead, canChangeLeadStatus, canEditLeadAutoDate, canEditProjectAutoDate, canViewSensitive, canViewClientInfo, reloadPerms: loadPerms }}>
      {children}
    </AuthContext.Provider>
  );
}
